// src/lib/offlineQueue.js
//
// A small durable queue for work that couldn't be sent because the person
// went offline while clocked in:
//   • "shots" — screenshots (the image itself) waiting to upload, and
//   • "ops"   — writes that must still happen, e.g. a clock-out.
//
// Stored in IndexedDB so it survives the tab being closed or the browser
// restarting; the next time the app opens online it picks up where it left
// off. If IndexedDB isn't available (private mode in some browsers) it falls
// back to memory, which still covers going offline and back within a visit.
//
// Every record carries the userId, and callers only ever read their own.

const DB_NAME = "techbugz-offline";
const STORES = ["shots", "ops"];

function openIdb() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") return reject(new Error("no indexedDB"));
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      STORES.forEach(s => { if (!req.result.objectStoreNames.contains(s)) req.result.createObjectStore(s, { keyPath: "id", autoIncrement: true }); });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

// ── backends ─────────────────────────────────────────────
function idbBackend(dbPromise) {
  const tx = async (store, mode, fn) => {
    const db = await dbPromise;
    return new Promise((resolve, reject) => {
      const t = db.transaction(store, mode);
      const out = fn(t.objectStore(store));
      t.oncomplete = () => resolve(out && "result" in out ? out.result : undefined);
      t.onerror = () => reject(t.error);
      t.onabort = () => reject(t.error);
    });
  };
  return {
    add: (store, value) => tx(store, "readwrite", s => s.add(value)),
    put: (store, value) => tx(store, "readwrite", s => s.put(value)),
    remove: (store, id) => tx(store, "readwrite", s => s.delete(id)),
    all: store => tx(store, "readonly", s => s.getAll()),
  };
}

function memoryBackend() {
  const data = { shots: [], ops: [] };
  let next = 1;
  return {
    add: async (store, value) => { const id = next++; data[store].push({ ...value, id }); return id; },
    put: async (store, value) => { const i = data[store].findIndex(x => x.id === value.id); if (i >= 0) data[store][i] = value; else data[store].push(value); },
    remove: async (store, id) => { data[store] = data[store].filter(x => x.id !== id); },
    all: async store => data[store].slice(),
  };
}

let backend = null;
function getBackend() {
  if (backend) return backend;
  if (typeof indexedDB === "undefined") return (backend = memoryBackend());
  const dbPromise = openIdb();
  // If opening fails, quietly switch to memory rather than lose screenshots.
  const idb = idbBackend(dbPromise);
  const mem = memoryBackend();
  let useMem = false;
  dbPromise.catch(() => { useMem = true; });
  const pick = () => (useMem ? mem : idb);
  const guard = name => async (...a) => {
    try { return await pick()[name](...a); }
    catch (err) {
      if (!useMem) {
        // Don't lose the screenshot over a storage problem — keep it in memory
        // for this visit, and say so (it won't survive closing the tab).
        console.warn("Offline storage isn't available, keeping queued items in memory only:", err?.message || err);
        useMem = true;
        return mem[name](...a);
      }
      throw err;
    }
  };
  return (backend = { add: guard("add"), put: guard("put"), remove: guard("remove"), all: guard("all") });
}

// Tests (or a future storage engine) can swap the backend.
export const _setBackend = b => { backend = b; };
export const _memoryBackend = memoryBackend;

// ── public API ───────────────────────────────────────────
const mine = (rows, userId) => rows.filter(r => r.userId === userId);

export const queueShot = shot => getBackend().add("shots", shot);
// Only updates a shot that's still queued — if another tab already uploaded and
// removed it, writing it back would bring it back from the dead.
export const updateShot = async shot => {
  const rows = await getBackend().all("shots");
  if (rows.some(r => r.id === shot.id)) await getBackend().put("shots", shot);
};
export const removeShot = id => getBackend().remove("shots", id);
export const pendingShots = async userId => mine(await getBackend().all("shots"), userId).sort((a, b) => a.capturedAt - b.capturedAt);

export const queueOp = op => getBackend().add("ops", op);
export const removeOp = id => getBackend().remove("ops", id);
export const pendingOps = async userId => mine(await getBackend().all("ops"), userId);

export const countPending = async userId => (await pendingShots(userId)).length + (await pendingOps(userId)).length;
